import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Gavel, TrendingUp, Trophy, AlertOctagon, Euro, Activity, Loader2, BarChart3, Download,
} from "lucide-react";
import api from "@/lib/api";
import { useReloadBus } from "@/lib/reloadBus";
import { AppHeader } from "@/components/AppHeader";

const RANGES = [
  { value: 7,  label: "7 Tage" },
  { value: 30, label: "30 Tage" },
  { value: 90, label: "90 Tage" },
  { value: 0,  label: "Gesamt" },
];

const _eur = (v) =>
  Number(v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 0 });

const _pct = (v) => `${Number(v || 0).toFixed(1)} %`;

export default function AdminAuctionStats() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get(`/admin/auction-stats?days=${days}`);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konnte Statistik nicht laden");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [days]);
  // Iter88zbu — Silent-Reload nach jeder Mutation.
  useReloadBus("*", () => load(true));

  const [downloading, setDownloading] = useState(false);
  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/admin/auction-stats.pdf?days=${days}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const range = days > 0 ? `${days}d` : "gesamt";
      a.download = `auktion-stats_${range}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF heruntergeladen");
    } catch (e) {
      toast.error(e.response?.data?.detail || "PDF-Download fehlgeschlagen");
    } finally {
      setDownloading(false);
    }
  };

  const kpis = data?.kpis || {};
  const pens = data?.penalties || {};
  const top = data?.top_installers || [];
  const timeline = data?.timeline || [];
  const tlMax = Math.max(1, ...timeline.map((t) => Math.max(t.auctions, t.bids)));

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="admin-auction-stats-page">
      <AppHeader title="Auktion-Statistik" back="/dashboard" />
      <main className="max-w-6xl mx-auto px-3 sm:px-5 py-5 space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <BarChart3 className="text-[#00E575]" size={22} />
            <div>
              <h1 className="font-display text-xl sm:text-2xl font-bold">Auktion-Statistik</h1>
              <p className="text-[12px] text-zinc-300 font-medium">KPIs zum Installateur-Marktplatz</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-xl bg-[#18181B] border border-zinc-800 overflow-hidden">
              {RANGES.map((r) => (
                <button
                  key={r.value}
                  onClick={() => setDays(r.value)}
                  data-testid={`stats-range-${r.value}`}
                  className={`px-3 h-9 text-[12px] font-bold uppercase tracking-wider ${days === r.value ? "bg-[#00E575] text-[#09090B]" : "text-zinc-400 hover:bg-zinc-800"}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <button
              onClick={downloadPdf}
              disabled={downloading || loading}
              data-testid="stats-download-pdf"
              className="h-9 px-3 rounded-xl bg-[#18181B] border border-zinc-800 text-[12px] font-bold uppercase tracking-wider text-zinc-300 hover:bg-zinc-800 inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              {downloading ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
              PDF-Report
            </button>
          </div>
        </div>

        {loading && (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 text-center text-zinc-400 inline-flex w-full justify-center gap-2">
            <Loader2 size={16} className="animate-spin" /> lädt…
          </div>
        )}

        {!loading && data && (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KpiCard icon={Gavel} label="Auktionen" value={kpis.total_auctions} hint={`${kpis.open} offen · ${kpis.awaiting_decision} wartend`} testid="kpi-total-auctions" />
              <KpiCard icon={Activity} label="Gebote" value={kpis.total_bids} hint={`Ø ${kpis.avg_bids_per_auction} / Auktion`} testid="kpi-total-bids" />
              <KpiCard icon={TrendingUp} label="Bid-Rate" value={_pct(kpis.bid_rate_pct)} hint={`${kpis.auctions_with_bids} mit Geboten`} testid="kpi-bid-rate" />
              <KpiCard icon={Trophy} label="Vergeben" value={kpis.awarded} hint={`Award-Rate ${_pct(kpis.award_rate_pct)}`} accent="green" testid="kpi-awarded" />
              <KpiCard icon={Euro} label="Auftragsvolumen" value={_eur(kpis.awarded_volume_eur)} hint={`Ø ${_eur(kpis.avg_winning_bid_eur)} / Auftrag`} accent="green" testid="kpi-volume" />
              <KpiCard icon={AlertOctagon} label="Ablehnungsrate" value={_pct(kpis.reject_rate_pct)} hint={`${pens.count} Pönalen`} accent="amber" testid="kpi-reject-rate" />
              <KpiCard icon={AlertOctagon} label="Pönalen offen" value={_eur(pens.pending_eur)} hint={`Mahnung gesendet: ${_eur(pens.invoiced_eur)}`} accent="amber" testid="kpi-pen-pending" />
              <KpiCard icon={Euro} label="Pönalen bezahlt" value={_eur(pens.paid_eur)} hint={`Gesamt: ${_eur(pens.total_eur)}`} testid="kpi-pen-paid" />
            </div>

            {/* Timeline */}
            <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5" data-testid="stats-timeline">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-display text-lg font-bold">Verlauf · letzte {timeline.length} Tage</h2>
                <div className="flex gap-3 text-[12px]">
                  <span className="inline-flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 bg-[#00E575] rounded-sm" /> Auktionen</span>
                  <span className="inline-flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 bg-sky-400 rounded-sm" /> Gebote</span>
                </div>
              </div>
              {timeline.length === 0 ? (
                <div className="text-zinc-500 text-sm text-center py-6">Keine Daten</div>
              ) : (
                <div className="flex items-end gap-1 h-40">
                  {timeline.map((t, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center gap-0.5 group relative">
                      <div className="w-full flex items-end gap-px h-32">
                        <div
                          className="flex-1 bg-[#00E575]/80 rounded-t-sm hover:bg-[#00E575] transition-colors"
                          style={{ height: `${(t.auctions / tlMax) * 100}%` }}
                          title={`${t.date}: ${t.auctions} Auktion(en)`}
                        />
                        <div
                          className="flex-1 bg-sky-400/80 rounded-t-sm hover:bg-sky-400 transition-colors"
                          style={{ height: `${(t.bids / tlMax) * 100}%` }}
                          title={`${t.date}: ${t.bids} Gebot(e)`}
                        />
                      </div>
                      <div className="text-[8px] text-zinc-500 -rotate-45 origin-top-left translate-y-2 whitespace-nowrap">{t.date.slice(5)}</div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Top Installers */}
            <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5" data-testid="stats-top-installers">
              <h2 className="font-display text-lg font-bold mb-3">Top-Installateure</h2>
              {top.length === 0 ? (
                <div className="text-zinc-500 text-sm text-center py-6">Noch keine Aktivität im Zeitraum.</div>
              ) : (
                <div className="overflow-x-auto -mx-5 px-5">
                  <table className="w-full text-[13px]">
                    <thead>
                      <tr className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold border-b border-zinc-800">
                        <th className="text-left py-2 font-bold">#</th>
                        <th className="text-left py-2 font-bold">Installateur</th>
                        <th className="text-right py-2 font-bold">Gewonnen</th>
                        <th className="text-right py-2 font-bold">Gebote</th>
                        <th className="text-right py-2 font-bold">Volumen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {top.map((row, i) => (
                        <tr key={row.user_id} className="border-b border-zinc-800/50 last:border-0" data-testid={`top-installer-row-${i}`}>
                          <td className="py-2 text-zinc-500 font-mono">{i + 1}</td>
                          <td className="py-2">
                            <div className="font-semibold text-zinc-100">{row.name}</div>
                            {row.company && <div className="text-[12px] text-zinc-300 font-medium">{row.company}</div>}
                          </td>
                          <td className="py-2 text-right font-bold text-[#00E575]">{row.wins}</td>
                          <td className="py-2 text-right text-zinc-300">{row.bids}</td>
                          <td className="py-2 text-right font-mono text-zinc-100">{_eur(row.total_eur)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, hint, accent, testid }) {
  const accentColor =
    accent === "green" ? "text-[#00E575]" :
    accent === "amber" ? "text-amber-300" :
    "text-zinc-100";
  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold font-bold">{label}</span>
        <Icon className={accentColor} size={14} />
      </div>
      <div className={`font-display text-2xl font-bold ${accentColor}`}>{value ?? "—"}</div>
      {hint && <div className="text-[12px] text-zinc-300 mt-1">{hint}</div>}
    </div>
  );
}
